import { Controller } from '@hotwired/stimulus';
// TODO: Consider replacing Popper.js with Floating UI for better performance and flexibility.
import { createPopper } from '@popperjs/core';
import { useClickOutside } from 'stimulus-use';
import { useHotkeys } from 'stimulus-use/hotkeys';

/* eslint-disable react-hooks/rules-of-hooks */
// Not React.

export default class extends Controller {
  // TODO: Add targets and values if needed in the future.
  initialize() {
    this.popoverTrigger = this.element;
    this.popoverContainer = document.querySelector(this.element.dataset.popoverContainer) || document.body;
    this.popoverTargetId = this.element.dataset.popoverTarget;
    this.popoverTargetClass = this.element.dataset.popoverTargetClass;
    this.popoverTitle = this.element.dataset.popoverTitle;
    this.popoverTitleClass = this.element.dataset.popoverTitleClass;
    this.popoverClose = this.element.dataset.popoverClose ?? false;
    this.popoverContentClass = this.element.dataset.popoverContentClass;
    this.popoverPlacement = this.element.dataset.popoverPlacement || 'bottom';
    this.popoverSrc = this.element.dataset.popoverContentSrc;
    this.srcContent = false;
  }

  connect() {
    this.createPopoverTarget();

    // ESLint likes to see `use*` functions as React hooks. They're not, so ESLint is disabled at the top of the file.
    useClickOutside(this, { element: this.popoverTarget });
    useHotkeys(this, {
      'esc': [this.hide],
    });

    this.popperInstance = createPopper(this.element, this.popoverTarget, {
      placement: this.popoverPlacement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8,
          },
        },
      ],
    });
  }

  disconnect() {
    this.popperInstance.destroy();

    this.popoverCloseEl.removeEventListener('click', this.handleCloseClick);
    this.popoverCloseEl.removeEventListener('keydown', this.handleCloseKeydown);

    this.popoverTarget.remove();
  }


  async show(event) {
    this.hideAll();

    if (event.code === 'Space') {
      event.preventDefault(); // Prevent scrolling the page when space is pressed.
    }

    if (this.popoverSrc) {
      if (!this.srcContent) {
        const response = await fetch(this.popoverSrc);
        if (response.ok) {
          this.popoverContentEl.innerHTML = await response.text();
          this.srcContent = true;
        }
      }
    }

    this.popoverTrigger.setAttribute('aria-expanded', true);

    this.popoverTarget.setAttribute('tabindex', 0);
    this.popoverTarget.setAttribute('aria-hidden', false);
    this.popoverTarget.focus();
    this.popperInstance.update();
  }

  hide() {
    if (this.popoverTarget.getAttribute('aria-hidden') === 'false') { // We don't need to hide if the popover isn't visible.
      this.popoverTarget.setAttribute('tabindex', -1);
      this.popoverTarget.setAttribute('aria-hidden', true);

      this.popoverTrigger.setAttribute('aria-expanded', false);
      this.popoverTrigger.focus();
    }
  }

  hideAll() {
    const popoverTargets = document.querySelectorAll('.popover');
    popoverTargets.forEach((target) => {
      target.blur();
      target.setAttribute('tabindex', -1);
      target.setAttribute('aria-hidden', true);
    });
  }

  clickOutside(event) { // eslint-disable-line no-unused-vars
    this.hide();
  }

  handleCloseClick(event) {
    event.currentTarget.blur();
    this.hide();
  }

  handleCloseKeydown(event) {
    if (['Enter', 'Escape'].includes(event.code)) {
      this.hide();
    }
  }

  createPopoverTarget() {
    this.popoverTarget = document.getElementById(this.popoverTargetId);
    this.popoverTrigger.setAttribute('aria-expanded', false);

    if (!this.popoverTarget) {
      this.popoverTarget = document.createElement('div');
      this.popoverTarget.id = this.popoverTargetId;
      this.popoverTarget.setAttribute('aria-labelledby', this.element.id);
      this.popoverTargetClass && this.popoverTarget.classList.add(this.popoverTargetClass);
      this.popoverContainer.appendChild(this.popoverTarget);
    }

    this.popoverTarget.classList.add('popover');
    this.popoverTarget.setAttribute('aria-hidden', true);
    this.popoverTarget.setAttribute('role', 'dialog');

    this.createPopoverArrow();
    this.createPopoverHeader(); // Must be handled before the Close button.
    this.createPopoverClose();
    this.createPopoverContent();
  }

  createPopoverArrow() {
    this.popoverArrow = this.popoverTarget.querySelector('[data-popper-arrow]');

    if (!this.popoverArrow) {
      this.popoverArrow = document.createElement('div');
      this.popoverArrow.setAttribute('data-popper-arrow', '');
      this.popoverTarget.appendChild(this.popoverArrow);
    }
  }

  createPopoverHeader() {
    if (this.popoverTitle) {
      this.popoverHeaderEl = this.popoverTarget.querySelector('.popover-header');
      this.popoverTitleEl = this.popoverTarget.querySelector('.popover-title');

      if (!this.popoverHeaderEl) {
        this.popoverHeaderEl = document.createElement('div');
        this.popoverHeaderEl.classList.add('popover-header');
        this.popoverTarget.appendChild(this.popoverHeaderEl);
      }

      if (!this.popoverTitleEl) {
        this.popoverTitleEl = document.createElement('div');
        this.popoverTitleEl.classList.add('popover-title');
        this.popoverTitleClass && this.popoverTitleEl.classList.add(this.popoverTitleClass);
        this.popoverTitleEl.innerText = this.popoverTitle;
        this.popoverHeaderEl.appendChild(this.popoverTitleEl);
      }
    }
  }

  createPopoverContent() {
    this.popoverContentEl = this.popoverTarget.querySelector('.popover-content');

    if (!this.popoverContentEl) {
      this.popoverContentEl = document.createElement('div');
      this.popoverContentEl.classList.add('popover-content');
      this.popoverContentClass && this.popoverContentEl.classList.add(this.popoverContentClass);
      this.popoverTarget.appendChild(this.popoverContentEl);
    }
  }

  createPopoverClose() {
    if (this.popoverClose) {
      this.popoverCloseEl = this.popoverTarget.querySelector('.popover-close');

      if (!this.popoverCloseEl) {
        this.popoverCloseEl = document.createElement('button');
        this.popoverCloseEl.innerText = 'Close';
        this.popoverCloseEl.classList.add('popover-close');
        this.popoverCloseEl.setAttribute('aria-label', 'Close');
        this.popoverCloseEl.setAttribute('type', 'button'); // default button type is submit
        this.popoverCloseEl.addEventListener('click', this.handleCloseClick.bind(this));
        this.popoverCloseEl.addEventListener('keydown', this.handleCloseKeydown.bind(this));

        if (this.popoverHeaderEl) {
          this.popoverHeaderEl.appendChild(this.popoverCloseEl);
        } else {
          this.popoverCloseEl.classList.add('popover-close-no-header');
          this.popoverTarget.appendChild(this.popoverCloseEl);
        }
      }
    }
  }
}
